// Печатът на издадена фактура — A4 (браузърът) и POS (ролката), изнесен от
// `InvoicePreviewModal`.
//
// Един оригинал на фактура, откъдето и да е прегледът (справките, Маркиране
// след записа, историята на сметките): докато оригиналът не е отпечатан нито
// веднъж (`original_printed_at` е null), и „Печат", и „Печат (POS)" печатат
// ОРИГИНАЛА; след това — само дубликати. До 0.0.390 така беше само в
// справките, а другаде всеки печат беше оригинал — POS и A4 от прегледа след
// записа даваха два оригинала (0.0.391 махна и въпроса „Печат през POS?“
// преди прегледа, `useInvoicingFlow`). Отпечатаният оригинал
// стига до BE и от двете места: POS печатът го отбелязва сам; A4 печата
// браузърът, който не казва дали е отпечатано или е натиснат „Отказ" — затова
// след диалога питаме „Отпечата ли се оригиналът?" и САМО при „Да" пращаме
// `POST /invoices/{id}/original-printed`. Не мине ли записът, въпросът остава
// отворен за нов опит — иначе следващият печат би бил втори оригинал.

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { printInvoicePos, type Invoice } from "../../../../../api/invoices";
import { useMarkInvoiceOriginalPrinted } from "../../../../../hooks/useInvoices";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useOrderingMessage } from "../../useOrderingMessage";
import { blockIfLicenseReadOnly } from "../../../../../lib/licenseReadOnly";

/** Отпечатан ли е оригиналът: по записа на BE или току-що — в този преглед
 *  (в Маркиране фактурата е локално копие и записът не стига до нея). */
export const isOriginalPrinted = (
  invoice: Pick<Invoice, "id" | "original_printed_at">,
  printedNowInvoiceId: number | null,
): boolean => invoice.original_printed_at != null || printedNowInvoiceId === invoice.id;

/**
 * A4 печатът: Ant Modal-ът е centered overlay → пречи на естествената
 * пагинация в print. Местим .invoice-paper директно в body, маркираме body с
 * .printing-invoice (CSS скрива всичко останало), пускаме print, и връщаме
 * paper-а на оригиналното му място (placeholder node пази точното място).
 * Името на PDF-а / dialog-а за печат идва от document.title — временно
 * „invoice-{номер}", след dialog-а се връща. `onRestored` — след dialog-а.
 */
const printPaper = (invoiceNumber: string, onRestored: () => void) => {
  const prevTitle = document.title;
  document.title = `invoice-${invoiceNumber}`;
  const paper = document.querySelector(".invoice-paper") as HTMLElement | null;
  let placeholder: Comment | null = null;
  let originalParent: HTMLElement | null = null;
  let wrapperTable: HTMLTableElement | null = null;
  if (paper && paper.parentElement) {
    originalParent = paper.parentElement;
    placeholder = document.createComment("invoice-paper-placeholder");
    originalParent.insertBefore(placeholder, paper);
    // Обвиваме paper-а в <table> с празен <thead>, който се повтаря
    // на всяка отпечатана страница (display: table-header-group) и
    // създава 16mm top margin без CSS @page margin → така няма
    // браузърски header/footer.
    wrapperTable = document.createElement("table");
    wrapperTable.className = "invoice-print-wrapper";
    const thead = document.createElement("thead");
    const trHead = document.createElement("tr");
    const thSpacer = document.createElement("th");
    thSpacer.setAttribute("aria-hidden", "true");
    trHead.appendChild(thSpacer);
    thead.appendChild(trHead);
    const tbody = document.createElement("tbody");
    const trBody = document.createElement("tr");
    const tdBody = document.createElement("td");
    tdBody.appendChild(paper);
    trBody.appendChild(tdBody);
    tbody.appendChild(trBody);
    wrapperTable.appendChild(thead);
    wrapperTable.appendChild(tbody);
    document.body.appendChild(wrapperTable);
    document.body.classList.add("printing-invoice");
  }
  const restore = () => {
    document.title = prevTitle;
    onRestored();
    document.body.classList.remove("printing-invoice");
    if (paper && placeholder && originalParent) {
      originalParent.insertBefore(paper, placeholder);
      placeholder.remove();
    }
    if (wrapperTable && wrapperTable.parentElement) {
      wrapperTable.parentElement.removeChild(wrapperTable);
    }
    window.removeEventListener("afterprint", restore);
  };
  window.addEventListener("afterprint", restore);
  requestAnimationFrame(() => window.print());
};

export const useInvoicePrint = (invoice: Invoice | null) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const queryClient = useQueryClient();
  const { notifyPrinterError } = usePrinterNotifications();
  const markOriginalPrinted = useMarkInvoiceOriginalPrinted();
  // Превключва заглавието „ФАКТУРА ОРИГИНАЛ" → „ФАКТУРА ДУБЛИКАТ"
  // само за времетраенето на print dialog-а. След print → restore.
  const [printAsDuplicate, setPrintAsDuplicate] = useState(false);
  const [isPrintingPos, setIsPrintingPos] = useState(false);
  // Въпросът е за конкретна фактура — затворен без отговор преглед не го
  // пренася върху следващата отворена.
  const [askingInvoiceId, setAskingInvoiceId] = useState<number | null>(null);
  const [printedNowInvoiceId, setPrintedNowInvoiceId] = useState<number | null>(null);

  const printsDuplicate = invoice != null && isOriginalPrinted(invoice, printedNowInvoiceId);

  const printA4 = () => {
    if (!invoice) return;
    // Браузърният печат не минава през BE — пазачът е единствената защита.
    if (blockIfLicenseReadOnly()) return;
    setPrintAsDuplicate(printsDuplicate);
    // Анулираната не се отбелязва (BE я отказва); отпечатаният — вече е.
    const asksAfterPrint = !printsDuplicate && !invoice.cancelled_at;
    printPaper(invoice.invoice_number, () => {
      setPrintAsDuplicate(false);
      if (asksAfterPrint) setAskingInvoiceId(invoice.id);
    });
  };

  const confirmOriginalPrinted = async () => {
    if (!invoice) return;
    if (blockIfLicenseReadOnly()) return;
    try {
      await markOriginalPrinted.mutateAsync(invoice.id);
      setPrintedNowInvoiceId(invoice.id);
      setAskingInvoiceId(null);
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, t("invoicePreview.originalPrintedSaveError")));
    }
  };

  const declineOriginalPrinted = () => setAskingInvoiceId(null);

  const printPos = async () => {
    if (!invoice || isPrintingPos) return;
    if (blockIfLicenseReadOnly()) return;
    const duplicate = printsDuplicate;
    setIsPrintingPos(true);
    try {
      await printInvoicePos(invoice.id, { duplicate });
      message.success(t("invoicePreview.printPosSuccess"));
      if (!duplicate) {
        setPrintedNowInvoiceId(invoice.id);
        queryClient.invalidateQueries({ queryKey: ["invoices", "detail", invoice.id] });
      }
    } catch (error: unknown) {
      notifyPrinterError(getApiErrorMessage(error, t("invoicePreview.printPosError")));
    } finally {
      setIsPrintingPos(false);
    }
  };

  return {
    printAsDuplicate,
    isPrintingPos,
    printA4,
    printPos,
    originalPrintedQuestion: {
      open: invoice != null && askingInvoiceId === invoice.id,
      saving: markOriginalPrinted.isPending,
      confirm: confirmOriginalPrinted,
      decline: declineOriginalPrinted,
    },
  };
};
